/**
 * Agrupamento das divergências da conferência (passo 7 — redesenho 05/08/2026,
 * pedido do dono: "agrupar por tipo de erro com a quantidade de cada, mostrar
 * as falhas de forma legível, ciência por erro com justificativa, e agrupar
 * falhas iguais para justificar em lote").
 *
 * Duas camadas, derivadas só dos dados da divergência (classe + alvo + texto):
 * - GRUPO = a classe (CADASTRAL, VALOR_DIVERGENTE, AUSENTE_NO_RETORNO, …);
 * - SUB-GRUPO = a "mesma natureza da falha", computada do ALVO
 *   (escopo|pessoa|eventoOuCampo|campo):
 *   · escopo evento/rubrica → o código do evento (todas as pessoas com o 291
 *     divergente caem juntas);
 *   · total_pessoa/agregado → o campo do total/agregado;
 *   · cadastral → o campo divergente (departamento, cargo, admissão, …);
 *   · contagem → um grupo só; alvos especiais (consistencia_txt,
 *     retencao_credito, saldo_darf) → um grupo por alvo.
 *   Função pura (testável) — a renderização fica na página.
 */

export interface DivergenciaItem {
  id: string;
  nivel: string;
  classe: string;
  severidade: string;
  status: string;
  alvo: string;
  descricao: string;
  calculado?: number | null;
  esperado?: number | null;
  delta?: number | null;
  unidade?: string | null;
  justificativa: string | null;
  referenciaEstudo: string | null;
}

export interface SubGrupoDivergencias {
  /** Chave da natureza (estável — usada como key). */
  chave: string;
  rotulo: string;
  itens: DivergenciaItem[];
  abertas: number;
  piorSeveridade: "bloqueante" | "alerta" | "informativo";
}

export interface GrupoDivergencias {
  classe: string;
  itens: DivergenciaItem[];
  abertas: number;
  bloqueantesAbertas: number;
  piorSeveridade: "bloqueante" | "alerta" | "informativo";
  subGrupos: SubGrupoDivergencias[];
}

function piorSeveridadeDe(itens: DivergenciaItem[]): SubGrupoDivergencias["piorSeveridade"] {
  const abertas = itens.filter((d) => d.status === "aberta");
  const base = abertas.length > 0 ? abertas : itens;
  if (base.some((d) => d.severidade === "bloqueante")) return "bloqueante";
  if (base.some((d) => d.severidade === "alerta")) return "alerta";
  return "informativo";
}

const ROTULO_ALVO_ESPECIAL: Record<string, string> = {
  consistencia_txt: "consistência interna do TXT",
  retencao_credito: "crédito de retenção de INSS s/ NF",
  saldo_darf: "saldo DARF",
};

/** Extrai "291 — VALE TRANSPORTE" da descrição "… evento 291 (VALE TRANSPORTE): …". */
function rotuloEvento(codigo: string, descricao: string): string {
  const m = descricao.match(/evento (\d+) \(([^)]+)\)/);
  if (m) return `evento ${m[1]} — ${m[2].trim()}`;
  return `evento ${codigo}`;
}

/** Chave + rótulo da "mesma natureza" de uma divergência. */
export function naturezaDaDivergencia(d: DivergenciaItem): { chave: string; rotulo: string } {
  const [escopo, _pessoa, terceiro, campo] = d.alvo.split("|");
  if (escopo === "cadastral") {
    const campoCad = terceiro || "";
    return campoCad
      ? { chave: `cadastral:${campoCad}`, rotulo: `campo "${campoCad}" divergente` }
      : { chave: "cadastral:pessoa-ausente", rotulo: "pessoa presente no retorno e ausente no cadastro" };
  }
  if (escopo === "evento") return { chave: `evento:${terceiro}`, rotulo: rotuloEvento(terceiro ?? "?", d.descricao) };
  if (escopo === "rubrica") return { chave: `rubrica:${terceiro}`, rotulo: `rubrica agregada ${terceiro}` };
  if (escopo === "total_pessoa") return { chave: `total_pessoa:${campo}`, rotulo: `total da pessoa — ${campo}` };
  if (escopo === "agregado") return { chave: `agregado:${campo}`, rotulo: `agregado — ${campo}` };
  if (escopo === "contagem") return { chave: "contagem", rotulo: "contagem de pessoas" };
  return { chave: `especial:${escopo}`, rotulo: ROTULO_ALVO_ESPECIAL[escopo] ?? escopo };
}

/** Agrupa por classe e sub-agrupa por natureza. Ordem: grupos com bloqueantes
 *  abertas primeiro; dentro, sub-grupos com abertas primeiro. */
export function agruparDivergencias(lista: DivergenciaItem[]): GrupoDivergencias[] {
  const porClasse = new Map<string, DivergenciaItem[]>();
  for (const d of lista) {
    const arr = porClasse.get(d.classe) ?? [];
    arr.push(d);
    porClasse.set(d.classe, arr);
  }
  const grupos: GrupoDivergencias[] = [];
  for (const [classe, itens] of porClasse) {
    const porNatureza = new Map<string, DivergenciaItem[]>();
    for (const d of itens) {
      const n = naturezaDaDivergencia(d);
      const arr = porNatureza.get(n.chave) ?? [];
      arr.push(d);
      porNatureza.set(n.chave, arr);
    }
    const subGrupos: SubGrupoDivergencias[] = [...porNatureza.entries()].map(([chave, its]) => ({
      chave,
      rotulo: naturezaDaDivergencia(its[0]!).rotulo,
      itens: its,
      abertas: its.filter((d) => d.status === "aberta").length,
      piorSeveridade: piorSeveridadeDe(its),
    }));
    subGrupos.sort((a, b) => b.abertas - a.abertas || a.rotulo.localeCompare(b.rotulo, "pt-BR"));
    grupos.push({
      classe,
      itens,
      abertas: itens.filter((d) => d.status === "aberta").length,
      bloqueantesAbertas: itens.filter((d) => d.status === "aberta" && d.severidade === "bloqueante").length,
      piorSeveridade: piorSeveridadeDe(itens),
      subGrupos,
    });
  }
  grupos.sort(
    (a, b) => b.bloqueantesAbertas - a.bloqueantesAbertas || b.abertas - a.abertas || a.classe.localeCompare(b.classe, "pt-BR"),
  );
  return grupos;
}
